import React, { memo } from 'react';
import { Button, Dropdown, Tag } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  MoreOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import AgentAvatar from '../../components/AgentAvatar';

const LANGUAGE_LABELS = {
  'zh-hans': '简体中文',
  en: 'English',
  fr: 'Français',
};

const getAgentTag = (agent) => {
  if (agent?.role?.trim()) return agent.role.trim();
  const name = agent?.name || '';
  const parts = name.split(/\s+/);
  if (parts.length > 1) return parts.slice(1).join(' ');
  return '';
};

const getAgentDisplayName = (agent) => {
  const name = agent?.name || '';
  const parts = name.split(/\s+/);
  return parts[0] || name;
};

const getAgentDescription = (agent) => {
  if (agent?.intro?.trim()) {
    const text = agent.intro.trim();
    return text.length > 72 ? `${text.slice(0, 72)}…` : text;
  }
  return '暂无开场白';
};

const AgentManageCard = memo(({
  agent,
  canEdit,
  canDelete,
  canManageMembers,
  onEdit,
  onDelete,
  onAccess,
  onManageMembers,
}) => {
  const displayName = getAgentDisplayName(agent);
  const tag = getAgentTag(agent);
  const desc = getAgentDescription(agent);
  const languageLabel = LANGUAGE_LABELS[agent.language] || agent.language || '-';
  const createdLabel = agent.created
    ? new Date(agent.created).toLocaleDateString('zh-CN')
    : '-';

  const menuItems = [
    {
      key: 'access',
      icon: <LinkOutlined />,
      label: '访问',
      onClick: () => onAccess(agent),
    },
  ];

  if (canEdit) {
    menuItems.push({
      key: 'edit',
      icon: <EditOutlined />,
      label: '编辑',
      onClick: () => onEdit(agent),
    });
  }

  if (canManageMembers) {
    menuItems.push({
      key: 'members',
      icon: <TeamOutlined />,
      label: '成员权限',
      onClick: () => onManageMembers(agent),
    });
  }

  if (canDelete) {
    menuItems.push({
      key: 'delete',
      icon: <DeleteOutlined />,
      label: '删除',
      danger: true,
      onClick: () => onDelete(agent),
    });
  }

  return (
    <article className="agent-manage-card">
      <div className="agent-manage-card__head">
        <div className="agent-manage-card__avatar-wrap">
          <AgentAvatar agent={agent} size={44} />
          <span className="agent-manage-card__online" aria-hidden />
        </div>
        <div className="agent-manage-card__main">
          <div className="agent-manage-card__name-row">
            <span className="agent-manage-card__name" title={agent.name}>{displayName}</span>
            {tag && <span className="agent-manage-card__tag" title={tag}>{tag}</span>}
          </div>
          <div className="agent-manage-card__desc">{desc}</div>
        </div>
      </div>

      <div className="agent-manage-card__meta">
        <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>{languageLabel}</Tag>
        <span className="agent-manage-card__meta-item">创建于 {createdLabel}</span>
      </div>

      <div className="agent-manage-card__apikey" title={agent.apikey || ''}>
        {agent.apikey || '未配置 API Key'}
      </div>

      <div className="agent-manage-card__actions">
        {canEdit && (
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(agent)}>
            编辑
          </Button>
        )}
        {canManageMembers && (
          <Button type="text" size="small" icon={<TeamOutlined />} onClick={() => onManageMembers(agent)}>
            成员
          </Button>
        )}
        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
          <Button type="text" size="small" icon={<MoreOutlined />} aria-label="更多操作" />
        </Dropdown>
      </div>
    </article>
  );
});

AgentManageCard.displayName = 'AgentManageCard';

export default AgentManageCard;
